import {
  FilterAction,
  FilterActions,
  PaymentFilter,
  VehicleFilterPricing,
} from "@modules/vehicle/types/vehicleFilter";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import Form from "@common/components/form";
import Slider from "@common/components/form/Slider";
import translate from "@common/helpers/translate";
import { useRouter } from "next/router";
import MoneyInput from "@common/components/form/MoneyInput";
import { PaymentFrequency } from "../types/PaymentFrequency";
import { PaymentFactors } from "../hooks/useVehicleList";

const inputDelayMs = 2000;

type Props = {
  onUpdate(filterAction: FilterAction): void;
  paymentFilter: PaymentFilter | undefined;
  pricing: VehicleFilterPricing;
  paymentFactors?: PaymentFactors;
};

const VehiclePaymentFilter = ({
  onUpdate,
  paymentFilter,
  pricing,
  paymentFactors,
}: Props) => {
  const { t } = useTranslation(["common", "vehiclesFilters"]);
  const { locale } = useRouter();

  const paymentFilterState = paymentFilter ? paymentFilter.state : null;

  const [minPaymentInputValue, setMinPaymentInputValue] = useState<number>(
    paymentFilterState !== null && paymentFilterState.minPayment != null
      ? paymentFilterState.minPayment
      : pricing.minPayment
  );
  const [maxPaymentInputValue, setMaxPaymentInputValue] = useState<number>(
    paymentFilterState !== null && paymentFilterState.maxPayment != null
      ? paymentFilterState.maxPayment
      : pricing.maxPayment
  );
  const [paymentFrequencyInputValue, setPaymentFrequencyInputValue] = useState(
    paymentFilterState !== null
      ? paymentFilterState.paymentFrequency
      : pricing.paymentFrequencyDefault
  );
  const [cashDownInputValue, setCashDownInputValue] = useState<number>(
    paymentFilterState !== null ? paymentFilterState.cashDown : pricing.cashDown
  );
  const [tradeInInputValue, setTradeInInputValue] = useState<number>(
    paymentFilterState !== null
      ? paymentFilterState.tradeInValue
      : pricing.tradeInValue
  );

  const [minPaymentTimer, setMinPaymentTimer] = useState<NodeJS.Timer>();
  const [maxPaymentTimer, setMaxPaymentTimer] = useState<NodeJS.Timer>();
  const [cashDownTimer, setCashDownTimer] = useState<NodeJS.Timer>();
  const [tradeInValueTimer, setTradeInValueTimer] = useState<NodeJS.Timer>();

  const updateSharedState = (
    minPayment: number,
    maxPayment: number,
    paymentFrequency: PaymentFrequency,
    cashDown: number,
    tradeInValue: number
  ) => {
    const isMinMaxDefault =
      minPayment === pricing.minPayment && maxPayment === pricing.maxPayment;

    onUpdate({
      type: FilterActions.AddPaymentFilter,
      payload: {
        minPayment: isMinMaxDefault ? null : minPayment,
        maxPayment: isMinMaxDefault ? null : maxPayment,
        paymentFrequency,
        cashDown,
        tradeInValue,
      },
    });
  };

  useEffect(() => {
    if (paymentFilterState !== null) {
      setMinPaymentInputValue(
        paymentFilterState.minPayment ?? pricing.minPayment
      );
      setMaxPaymentInputValue(
        paymentFilterState.maxPayment ?? pricing.maxPayment
      );
      setPaymentFrequencyInputValue(paymentFilterState.paymentFrequency);
      setCashDownInputValue(paymentFilterState.cashDown);
      setTradeInInputValue(paymentFilterState.tradeInValue);
    } else {
      setMinPaymentInputValue(pricing.minPayment);
      setMaxPaymentInputValue(pricing.maxPayment);
      setPaymentFrequencyInputValue(
        paymentFactors?.paymentFrequency ?? pricing.paymentFrequencyDefault
      );
      setCashDownInputValue(paymentFactors?.cashDown ?? pricing.cashDown);
      setTradeInInputValue(
        paymentFactors?.tradeInValue ?? pricing.tradeInValue
      );
    }
  }, [
    pricing.minPayment,
    pricing.maxPayment,
    paymentFilterState,
    pricing.paymentFrequencyDefault,
    pricing.cashDown,
    pricing.tradeInValue,
    paymentFactors,
  ]);

  const onChangeMinPayment = (strippedValue: number) => {
    if (!Number.isNaN(strippedValue)) {
      setMinPaymentInputValue(strippedValue);

      clearTimeout(minPaymentTimer);

      const newTimer = setTimeout(() => {
        if (
          strippedValue <= maxPaymentInputValue &&
          strippedValue >= pricing.minPayment
        ) {
          updateSharedState(
            strippedValue,
            maxPaymentInputValue,
            paymentFrequencyInputValue,
            cashDownInputValue,
            tradeInInputValue
          );
        } else {
          setMinPaymentInputValue(
            paymentFilterState?.minPayment ?? pricing.minPayment
          );
        }
      }, inputDelayMs);

      setMinPaymentTimer(newTimer);
    }
  };

  const onChangeMaxPayment = (strippedValue: number) => {
    if (!Number.isNaN(strippedValue)) {
      setMaxPaymentInputValue(strippedValue);

      clearTimeout(maxPaymentTimer);

      const newTimer = setTimeout(() => {
        if (
          strippedValue >= minPaymentInputValue &&
          strippedValue <= pricing.maxPayment
        ) {
          updateSharedState(
            minPaymentInputValue,
            strippedValue,
            paymentFrequencyInputValue,
            cashDownInputValue,
            tradeInInputValue
          );
        } else {
          setMaxPaymentInputValue(
            paymentFilterState?.maxPayment ?? pricing.maxPayment
          );
        }
      }, inputDelayMs);

      setMaxPaymentTimer(newTimer);
    }
  };

  const onChangeCashDown = (strippedValue: number) => {
    if (!Number.isNaN(strippedValue)) {
      setCashDownInputValue(strippedValue);

      clearTimeout(cashDownTimer);

      const newTimer = setTimeout(() => {
        updateSharedState(
          minPaymentInputValue,
          maxPaymentInputValue,
          paymentFrequencyInputValue,
          strippedValue,
          tradeInInputValue
        );
      }, inputDelayMs);

      setCashDownTimer(newTimer);
    }
  };

  const onChangeTradeInValue = (strippedValue: number) => {
    if (!Number.isNaN(strippedValue)) {
      setTradeInInputValue(strippedValue);

      clearTimeout(tradeInValueTimer);

      const newTimer = setTimeout(() => {
        updateSharedState(
          minPaymentInputValue,
          maxPaymentInputValue,
          paymentFrequencyInputValue,
          cashDownInputValue,
          strippedValue
        );
      }, inputDelayMs);

      setTradeInValueTimer(newTimer);
    }
  };

  const onAfterSliderChange = (
    sliderChangeValue: number | readonly number[]
  ) => {
    const sliderChangeValueArray = sliderChangeValue as number[];
    const minPaymentValue = sliderChangeValueArray[0];
    const maxPaymentValue = sliderChangeValueArray[1];

    if (minPaymentValue !== undefined && maxPaymentValue !== undefined) {
      updateSharedState(
        minPaymentValue,
        maxPaymentValue,
        paymentFrequencyInputValue,
        cashDownInputValue,
        tradeInInputValue
      );
    }
  };

  const onSliderChange = (sliderChangeValue: number | readonly number[]) => {
    const sliderChangeValueArray = sliderChangeValue as number[];
    const minPaymentValue = sliderChangeValueArray[0];
    const maxPaymentValue = sliderChangeValueArray[1];

    if (minPaymentValue !== undefined && maxPaymentValue !== undefined) {
      setMinPaymentInputValue(minPaymentValue);
      setMaxPaymentInputValue(maxPaymentValue);
    }
  };

  const handlePaymentFrequencyChange = (paymentFrequency: PaymentFrequency) => {
    setPaymentFrequencyInputValue(paymentFrequency);
    updateSharedState(
      minPaymentInputValue,
      maxPaymentInputValue,
      paymentFrequency,
      cashDownInputValue,
      tradeInInputValue
    );
  };

  return (
    <>
      <div className="px-3 mt-5">
        <span className="body-3">{t("common:payment_frequency")}</span>

        <div className="flex mt-2 px-0.5 space-x-5">
          {pricing.paymentFrequencies.map((pf) => (
            <Form.Radio
              key={pf.id}
              id={`pf-${pf.id.toString()}`}
              name="paymentFreq"
              label={translate(pf.name, locale || "")}
              checked={paymentFrequencyInputValue.id === pf.id}
              onChange={() => handlePaymentFrequencyChange(pf)}
            />
          ))}
        </div>
      </div>

      <div className="bg-gray-25 mt-5 p-4 rounded-lg">
        <span className="body-3">{t("vehiclesFilters:shop_by_payment")}</span>

        <div className="mt-4">
          <div className="flex gap-4">
            <div
              className={`w-full ${
                minPaymentInputValue > pricing.minPayment
                  ? "text-primary-bold"
                  : ""
              }`}
            >
              <MoneyInput
                id="minimumPaymentFilter"
                label={t("common:min")}
                onChange={onChangeMinPayment}
                value={minPaymentInputValue}
                locale={locale || ""}
              />
            </div>

            <div
              className={`w-full ${
                maxPaymentInputValue < pricing.maxPayment
                  ? "text-primary-bold"
                  : ""
              }`}
            >
              <MoneyInput
                id="maximumPaymentFilter"
                label={t("common:max")}
                onChange={onChangeMaxPayment}
                value={maxPaymentInputValue}
                locale={locale || ""}
              />
            </div>
          </div>

          <div className="pt-2">
            <Slider
              className="horizontal-slider"
              min={pricing.minPayment}
              max={pricing.maxPayment}
              value={[minPaymentInputValue, maxPaymentInputValue]}
              defaultValue={[pricing.minPayment, pricing.maxPayment]}
              onAfterChange={onAfterSliderChange}
              onChange={onSliderChange}
              step={10}
              formatDisplayValue="money"
              locale={locale}
              ariaLabel="Vehicle Payment Slider"
            />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-[6fr_5fr] grid-rows-2 gap-x-4 gap-y-0.5">
          <label className="caption-1 self-center" htmlFor="cashDown">
            {t("vehiclesFilters:cash_down")}
          </label>
          <MoneyInput
            id="cashDown"
            onChange={onChangeCashDown}
            value={cashDownInputValue}
            locale={locale || ""}
          />

          <label className="caption-1 self-center" htmlFor="tradeInValue">
            {t("vehiclesFilters:trade_in_value")}
          </label>
          <MoneyInput
            id="tradeInValue"
            className="trade-in-filter-input"
            onChange={onChangeTradeInValue}
            value={tradeInInputValue}
            locale={locale || ""}
          />
        </div>
      </div>
    </>
  );
};

export default VehiclePaymentFilter;
